import { useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import { CopyableId } from "../../shared/ui/CopyableId"
import { SelectField } from "../../shared/ui/SelectField"
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog"
import { Avatar } from "../../shared/ui/Avatar"
import {
  listAdminUsersApi,
  createMentorApi,
  deleteAdminUserApi,
  banAdminUserApi,
  unbanAdminUserApi,
  AdminUserItem,
  AdminUserRole,
  UpdateMentorPayload,
  updateMentorApi,
  uploadMentorAvatarApi,
} from "../../features/users/api/profile.api"
import {
  getUserPushTokensApi,
  revokeUserPushTokensApi,
  PushToken,
} from "../../features/notifications/api/notifications.api"
import {
  PlusIcon,
  PencilSquareIcon,
  UserIcon,
  Squares2X2Icon,
  ListBulletIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  EyeIcon,
  IdentificationIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EnvelopeIcon,
  CalendarIcon,
  NoSymbolIcon,
  TrashIcon,
  LockOpenIcon,
  BellIcon,
  BellSlashIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

type ViewMode = "table" | "cards"
type DrawerMode = "create" | "edit" | "view"

const LIMIT = 12

const LEVEL_CONFIG: Record<string, { label: string; className: string }> = {
  beginner: { label: "Новичок", className: "bg-green-50 text-green-700 ring-1 ring-green-200" },
  middle:   { label: "Средний", className: "bg-blue-50 text-blue-700 ring-1 ring-blue-200" },
  expert:   { label: "Эксперт", className: "bg-purple-50 text-purple-700 ring-1 ring-purple-200" },
}

const ROLE_CONFIG: Record<AdminUserRole, { label: string; className: string }> = {
  user:   { label: "Пользователь", className: "bg-gray-100 text-gray-600 ring-1 ring-gray-200" },
  mentor: { label: "Ментор",       className: "bg-violet-50 text-violet-700 ring-1 ring-violet-200" },
  admin:  { label: "Админ",        className: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
}

const CITY_OPTIONS = [
  { value: "bishkek", label: "Бишкек" },
  { value: "almaty", label: "Алматы" },
  { value: "astana", label: "Астана" },
  { value: "moscow", label: "Москва" },
  { value: "dubai", label: "Дубай" },
]

const INDUSTRY_OPTIONS = [
  { value: "marketing", label: "Маркетинг" },
  { value: "smm", label: "SMM" },
  { value: "design", label: "Дизайн" },
  { value: "target", label: "Таргет" },
  { value: "development", label: "Разработка" },
]

const formatDate = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })

const LevelBadge = ({ level }: { level?: string | null }) => {
  if (!level) return <span className="text-gray-300 text-xs">—</span>
  const cfg = LEVEL_CONFIG[level] ?? { label: level, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const RoleBadge = ({ role }: { role: AdminUserRole }) => {
  const cfg = ROLE_CONFIG[role] ?? { label: role, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const StatusBadge = ({ banned }: { banned: boolean }) =>
  banned ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-200">
      <NoSymbolIcon className="w-3 h-3" />
      Забанен
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Активен
    </span>
  )

const PushBadge = ({ enabled }: { enabled?: boolean }) => {
  if (enabled === undefined) return null
  return enabled ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
      <BellIcon className="w-3 h-3" />
      Push включены
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 ring-1 ring-gray-200">
      <BellSlashIcon className="w-3 h-3" />
      Push выключены
    </span>
  )
}

const PLATFORM_LABELS: Record<string, string> = { ios: "iOS", android: "Android", web: "Web" }

const formatTokenDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })


export const UsersPage = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("table")
  const [users, setUsers] = useState<AdminUserItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)

  const [pendingSearch, setPendingSearch] = useState("")
  const [search, setSearch] = useState("")
  const [roleFilter, setRoleFilter] = useState<AdminUserRole | "">("")
  const [statusFilter, setStatusFilter] = useState<"banned" | "active" | "">("")

  /* Drawer */
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("create")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<AdminUserItem | null>(null)

  /* Create form */
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [phone, setPhone] = useState("")

  /* Edit form */
  const [editData, setEditData] = useState<UpdateMentorPayload>({})
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  /* Delete */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState("")

  /* Ban / unban */
  const [banToggling, setBanToggling] = useState(false)
  const [banError, setBanError] = useState("")

  /* Push devices */
  const [pushTokens, setPushTokens] = useState<PushToken[] | null>(null)
  const [pushTokensLoading, setPushTokensLoading] = useState(false)
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [revokeError, setRevokeError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (page - 1) * LIMIT
      const res = await listAdminUsersApi({
        limit: LIMIT,
        offset,
        role: roleFilter,
        is_banned: statusFilter === "banned" ? true : statusFilter === "active" ? false : undefined,
        search: search || undefined,
      })
      setUsers(res.data.items)
      setTotal(res.data.total)
    } finally {
      setLoading(false)
    }
  }, [page, roleFilter, statusFilter, search])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [roleFilter, statusFilter, search])

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, total)

  const handleSearch = () => setSearch(pendingSearch.trim())

  const openCreate = () => {
    setDrawerMode("create")
    setEmail(""); setPassword(""); setPhone("")
    setDrawerOpen(true)
  }

  const openEdit = (u: AdminUserItem) => {
    setDrawerMode("edit")
    setEditTarget(u)
    setEditData({ full_name: u.full_name, city: u.city, industry: u.industry, experience_level: u.experience_level })
    setDrawerOpen(true)
  }

  const openView = (u: AdminUserItem) => {
    setDrawerMode("view")
    setEditTarget(u)
    setDrawerOpen(true)
  }

  const openRow = (u: AdminUserItem) => (u.role === "mentor" ? openEdit(u) : openView(u))

  const closeDrawer = () => {
    setDrawerOpen(false)
    setDeleteConfirmOpen(false)
    setDeleteError("")
    setBanError("")
    setRevokeConfirmOpen(false)
    setRevokeError("")
    setPushTokens(null)
  }

  useEffect(() => {
    if (!drawerOpen || drawerMode === "create" || !editTarget) return
    setPushTokensLoading(true)
    getUserPushTokensApi(editTarget.user_id)
      .then((res) => setPushTokens(res.data.items))
      .catch(() => setPushTokens([]))
      .finally(() => setPushTokensLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, drawerMode, editTarget?.user_id])

  const handleRevokeTokens = async () => {
    if (!editTarget) return
    setRevoking(true)
    setRevokeError("")
    try {
      await revokeUserPushTokensApi(editTarget.user_id)
      setPushTokens([])
      setRevokeConfirmOpen(false)
    } catch {
      setRevokeError("Не удалось отозвать устройства")
    } finally {
      setRevoking(false)
    }
  }

  const handleCreate = async () => {
    setSaving(true)
    try {
      await createMentorApi({ email, password, phone_number: phone || undefined })
      setPage(1)
      await load()
      closeDrawer()
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!editTarget) return
    setSaving(true)
    try {
      await updateMentorApi(editTarget.user_id, editData)
      setUsers((prev) => prev.map((u) => (u.user_id === editTarget.user_id ? { ...u, ...editData } : u)))
      closeDrawer()
    } finally {
      setSaving(false)
    }
  }

  const handleToggleBan = async () => {
    if (!editTarget) return
    setBanToggling(true)
    setBanError("")
    const nextBanned = !editTarget.is_banned
    try {
      if (nextBanned) {
        await banAdminUserApi(editTarget.user_id)
      } else {
        await unbanAdminUserApi(editTarget.user_id)
      }
      setEditTarget((prev) => (prev ? { ...prev, is_banned: nextBanned } : prev))
      setUsers((prev) => prev.map((u) => (u.user_id === editTarget.user_id ? { ...u, is_banned: nextBanned } : u)))
    } catch {
      setBanError(nextBanned ? "Не удалось забанить пользователя" : "Не удалось разбанить пользователя")
    } finally {
      setBanToggling(false)
    }
  }

  const handleDelete = async () => {
    if (!editTarget) return
    setDeleting(true)
    setDeleteError("")
    try {
      await deleteAdminUserApi(editTarget.user_id)
      setUsers((prev) => prev.filter((u) => u.user_id !== editTarget.user_id))
      setTotal((t) => Math.max(0, t - 1))
      setDeleteConfirmOpen(false)
      closeDrawer()
    } catch {
      setDeleteError("Не удалось удалить пользователя")
    } finally {
      setDeleting(false)
    }
  }

  const handleUploadAvatar = async (file: File) => {
    if (!editTarget) return
    setAvatarUploading(true)
    try {
      await uploadMentorAvatarApi(editTarget.user_id, file)
    } finally {
      setAvatarUploading(false)
    }
  }

  const activeFiltersCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0) + (search ? 1 : 0)

  return (
    <DashboardLayout title="Пользователи">

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex flex-1 min-w-[220px] max-w-sm items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Поиск по email или имени…"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 text-sm outline-none"
          />
        </div>

        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
        >
          Найти
        </button>

        <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

        <FilterDropdown
          label="Роль"
          value={roleFilter}
          onChange={setRoleFilter}
          options={(Object.keys(ROLE_CONFIG) as AdminUserRole[]).map((r) => ({ value: r, label: ROLE_CONFIG[r].label }))}
        />

        <FilterDropdown
          label="Статус"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "active", label: "Активен", dot: "bg-emerald-400" },
            { value: "banned", label: "Забанен", dot: "bg-red-400" },
          ]}
        />

        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={() => { setRoleFilter(""); setStatusFilter(""); setSearch(""); setPendingSearch("") }}
            className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
          >
            Сбросить фильтры
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center bg-gray-100 p-1 rounded-lg">
            <button onClick={() => setViewMode("table")} title="Таблица"
              className={`p-1.5 rounded-md transition-all ${viewMode === "table" ? "bg-white shadow-sm text-gray-700" : "text-gray-400 hover:text-gray-600"}`}>
              <ListBulletIcon className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode("cards")} title="Карточки"
              className={`p-1.5 rounded-md transition-all ${viewMode === "cards" ? "bg-white shadow-sm text-gray-700" : "text-gray-400 hover:text-gray-600"}`}>
              <Squares2X2Icon className="w-4 h-4" />
            </button>
          </div>

          <button onClick={openCreate}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
            <PlusIcon className="w-4 h-4" />
            Добавить ментора
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">

        {!loading && (
          <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50/60">
            <span className="text-xs text-gray-400">
              {total > 0 ? <>Показаны {from}–{to} из <span className="font-medium text-gray-600">{total}</span></> : "Ничего не найдено"}
            </span>
          </div>
        )}

        {/* Table view */}
        {viewMode === "table" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Пользователь</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Роль</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><MapPinIcon className="w-3.5 h-3.5" />Город</span>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Уровень</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" />Регистрация</span>
                  </th>
                  <th className="px-5 py-3 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse" />
                          <div className="h-4 bg-gray-100 rounded animate-pulse w-32" />
                        </div>
                      </td>
                      {[1,2,3,4,5].map((j) => (
                        <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-16" /></td>
                      ))}
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <UserIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Пользователи не найдены</p>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.user_id}
                      onClick={() => openRow(u)}
                      className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.full_name || u.email} src={u.avatar} size={36} />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 truncate max-w-[180px]">{u.full_name ?? <span className="text-gray-300 italic">Без имени</span>}</p>
                            <p className="text-xs text-gray-400 truncate max-w-[180px]">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5"><RoleBadge role={u.role} /></td>
                      <td className="px-5 py-3.5"><StatusBadge banned={u.is_banned} /></td>
                      <td className="px-5 py-3.5 text-gray-600 capitalize">{u.city ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-5 py-3.5"><LevelBadge level={u.experience_level} /></td>
                      <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{formatDate(u.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg opacity-0 group-hover:opacity-100">
                          {u.role === "mentor" ? <PencilSquareIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
                          {u.role === "mentor" ? "Редактировать" : "Просмотр"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards view */}
        {viewMode === "cards" && (
          <div className="p-5">
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="border rounded-xl p-4 space-y-3 animate-pulse">
                    <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto" />
                    <div className="h-4 bg-gray-100 rounded w-3/4 mx-auto" />
                    <div className="h-3 bg-gray-100 rounded w-1/2 mx-auto" />
                  </div>
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="py-16 text-center">
                <UserIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Пользователи не найдены</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {users.map((u) => (
                  <div key={u.user_id}
                    onClick={() => openRow(u)}
                    className="relative border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group flex flex-col items-center text-center gap-2 cursor-pointer">
                    {u.is_banned && (
                      <span className="absolute top-2.5 right-2.5">
                        <NoSymbolIcon className="w-4 h-4 text-red-400" />
                      </span>
                    )}
                    <Avatar name={u.full_name || u.email} src={u.avatar} size={64} />
                    <div className="min-w-0 w-full">
                      <p className="font-semibold text-gray-800 text-sm leading-tight truncate">
                        {u.full_name ?? <span className="text-gray-300 italic font-normal">Без имени</span>}
                      </p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{u.email}</p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-1">
                      <RoleBadge role={u.role} />
                      {u.city && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full capitalize">
                          <MapPinIcon className="w-3 h-3 text-gray-400" />{u.city}
                        </span>
                      )}
                      {u.industry && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full capitalize">
                          <BriefcaseIcon className="w-3 h-3 text-gray-400" />{u.industry}
                        </span>
                      )}
                    </div>
                    {u.experience_level && <LevelBadge level={u.experience_level} />}
                    <p className="text-[11px] text-gray-300 mt-0.5">С нами с {formatDate(u.created_at)}</p>
                    <span className="mt-0.5 text-xs text-gray-400 group-hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100 inline-flex items-center gap-1">
                      {u.role === "mentor" ? <PencilSquareIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
                      {u.role === "mentor" ? "Редактировать" : "Просмотр"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Назад
              </button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            <>
              {/* Header */}
              <div className={`bg-gradient-to-r ${
                drawerMode === "create" ? "from-blue-600 to-blue-500" : drawerMode === "edit" ? "from-violet-600 to-purple-500" : "from-slate-600 to-slate-500"
              } px-6 pt-6 pb-8 flex-shrink-0 relative`}>
                <button onClick={closeDrawer}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <XMarkIcon className="w-4 h-4 text-white" />
                </button>

                <div className="flex items-center gap-2 mb-3">
                  <UserIcon className="w-5 h-5 text-white/80" />
                  <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                    {drawerMode === "create" ? "Новый ментор" : drawerMode === "edit" ? "Редактировать ментора" : "Профиль пользователя"}
                  </span>
                </div>

                {(drawerMode === "edit" || drawerMode === "view") && editTarget ? (
                  <>
                    <div className="flex items-center gap-3 pr-10">
                      <Avatar name={editTarget.full_name || editTarget.email} src={editTarget.avatar} size={64} />
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-lg leading-tight truncate">
                          {editTarget.full_name ?? <span className="text-white/60 italic">Без имени</span>}
                        </p>
                        <p className="text-white/70 text-sm mt-0.5 truncate">{editTarget.email}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <RoleBadge role={editTarget.role} />
                      <StatusBadge banned={editTarget.is_banned} />
                      <PushBadge enabled={editTarget.push_enabled} />
                    </div>
                  </>
                ) : (
                  <p className="text-white font-semibold text-lg">Создание аккаунта ментора</p>
                )}
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                {(drawerMode === "view" || drawerMode === "edit") && editTarget && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <DevicePhoneMobileIcon className="w-3.5 h-3.5" />
                        Push-устройства
                      </p>
                      {!!pushTokens?.length && (
                        <button
                          type="button"
                          onClick={() => setRevokeConfirmOpen(true)}
                          className="text-xs font-medium text-red-500 hover:text-red-600"
                        >
                          Отозвать все
                        </button>
                      )}
                    </div>

                    {revokeError && (
                      <div className="px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                        {revokeError}
                      </div>
                    )}

                    {pushTokensLoading ? (
                      <div className="space-y-1.5">
                        {[1, 2].map((i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}
                      </div>
                    ) : !pushTokens || pushTokens.length === 0 ? (
                      <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3 text-xs text-gray-400">
                        Нет привязанных устройств
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
                        {pushTokens.map((t) => (
                          <div key={t.id} className="flex items-center justify-between px-4 py-2.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                              <span className="text-xs font-medium text-gray-600">{PLATFORM_LABELS[t.platform] ?? t.platform}</span>
                              <span className="font-mono text-[11px] text-gray-400 truncate">{t.token.slice(0, 14)}…</span>
                            </div>
                            <span className="text-[11px] text-gray-400 flex-shrink-0">{formatTokenDate(t.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {drawerMode === "view" && editTarget ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <IdentificationIcon className="w-3.5 h-3.5" />
                      Информация
                    </p>
                    <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
                      <InfoRow icon={<EnvelopeIcon className="w-3.5 h-3.5" />} label="Email" value={editTarget.email} />
                      <InfoRow icon={<PhoneIcon className="w-3.5 h-3.5" />} label="Телефон" value={editTarget.phone_number} />
                      <InfoRow icon={<MapPinIcon className="w-3.5 h-3.5" />} label="Город" value={editTarget.city} capitalize />
                      <InfoRow icon={<BuildingOfficeIcon className="w-3.5 h-3.5" />} label="Индустрия" value={editTarget.industry} capitalize />
                      <div className="flex items-center justify-between px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs text-gray-400">Уровень</span>
                        <LevelBadge level={editTarget.experience_level} />
                      </div>
                      <InfoRow icon={<CalendarIcon className="w-3.5 h-3.5" />} label="Регистрация" value={formatDate(editTarget.created_at)} />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">ID</p>
                      <CopyableId value={editTarget.user_id} length={20} />
                    </div>
                  </div>
                ) : drawerMode === "create" ? (
                  <>
                    <Field label="Email">
                      <input value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="mentor@example.com" type="email"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300" />
                    </Field>
                    <Field label="Пароль">
                      <input value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••" type="password"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300" />
                    </Field>
                    <Field label={<>Телефон <span className="normal-case text-gray-400 font-normal">(необязательно)</span></>}>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="+7 000 000 00 00"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300" />
                    </Field>
                  </>
                ) : (
                  <>
                    <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
                      <InfoRow icon={<CalendarIcon className="w-3.5 h-3.5" />} label="Регистрация" value={editTarget ? formatDate(editTarget.created_at) : undefined} />
                    </div>

                    {/* Avatar upload */}
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Аватар</p>
                      <label className={`cursor-pointer flex items-center gap-3 px-4 py-3 border-2 border-dashed rounded-xl transition-colors ${avatarUploading ? "border-gray-200 bg-gray-50" : "border-gray-200 hover:border-blue-400 hover:bg-blue-50"}`}>
                        {avatarUploading ? (
                          <>
                            <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                            <span className="text-sm text-gray-400">Загрузка…</span>
                          </>
                        ) : (
                          <>
                            <PlusIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-500">Загрузить фото</span>
                          </>
                        )}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadAvatar(f) }} />
                      </label>
                    </div>

                    <Field label="Полное имя">
                      <input value={editData.full_name ?? ""} onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                        placeholder="Иван Иванов"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300" />
                    </Field>

                    <Field label="Город">
                      <SelectField
                        value={editData.city ?? ""}
                        onChange={(v) => setEditData({ ...editData, city: v || null })}
                        placeholder="Выберите город"
                        options={CITY_OPTIONS}
                      />
                    </Field>

                    <Field label="Индустрия">
                      <SelectField
                        value={editData.industry ?? ""}
                        onChange={(v) => setEditData({ ...editData, industry: v || null })}
                        placeholder="Выберите индустрию"
                        options={INDUSTRY_OPTIONS}
                      />
                    </Field>

                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Уровень опыта</p>
                      <div className="grid grid-cols-3 gap-2">
                        {(["beginner", "middle", "expert"] as const).map((lvl) => {
                          const active = editData.experience_level === lvl
                          return (
                            <button key={lvl} onClick={() => setEditData({ ...editData, experience_level: active ? null : lvl })}
                              className={`py-2.5 rounded-xl text-sm font-medium border transition-all capitalize ${
                                active
                                  ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                  : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                              }`}>
                              {LEVEL_CONFIG[lvl].label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 space-y-2">
                {(deleteError || banError) && (
                  <div className="px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                    {deleteError || banError}
                  </div>
                )}
                <div className="flex gap-2">
                  {drawerMode === "view" ? (
                    <button onClick={closeDrawer}
                      className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                      Закрыть
                    </button>
                  ) : (
                    <>
                      <button onClick={closeDrawer}
                        className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                        Отмена
                      </button>
                      <button
                        disabled={saving || avatarUploading || (drawerMode === "create" && (!email || !password))}
                        onClick={drawerMode === "create" ? handleCreate : handleSaveEdit}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                      >
                        {saving
                          ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          : <CheckCircleIcon className="w-4 h-4" />}
                        {drawerMode === "create" ? "Создать" : "Сохранить"}
                      </button>
                    </>
                  )}
                </div>

                {(drawerMode === "view" || drawerMode === "edit") && editTarget && editTarget.role !== "admin" && (
                  <div className="flex gap-2">
                    <button
                      disabled={banToggling}
                      onClick={handleToggleBan}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-40 ${
                        editTarget.is_banned
                          ? "text-emerald-600 hover:bg-emerald-50"
                          : "text-amber-600 hover:bg-amber-50"
                      }`}
                    >
                      {banToggling ? (
                        <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      ) : editTarget.is_banned ? (
                        <LockOpenIcon className="w-4 h-4" />
                      ) : (
                        <NoSymbolIcon className="w-4 h-4" />
                      )}
                      {editTarget.is_banned ? "Разбанить" : "Забанить"}
                    </button>
                    <button
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Удалить
                    </button>
                  </div>
                )}
              </div>
            </>
          </div>
        </>,
        document.body
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Удалить пользователя?"
        message={
          editTarget
            ? `Аккаунт «${editTarget.full_name ?? editTarget.email}» будет удалён без возможности восстановления.`
            : ""
        }
        confirmLabel="Удалить"
        loading={deleting}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <ConfirmDialog
        open={revokeConfirmOpen}
        title="Отозвать все устройства?"
        message={
          editTarget
            ? `Все push-устройства пользователя «${editTarget.full_name ?? editTarget.email}» будут отвязаны. Ему нужно будет заново авторизовать push на устройстве.`
            : ""
        }
        confirmLabel="Отозвать"
        loading={revoking}
        danger
        onConfirm={handleRevokeTokens}
        onCancel={() => setRevokeConfirmOpen(false)}
      />
    </DashboardLayout>
  )
}

/* ── Field wrapper ── */
const Field = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">{label}</label>
    {children}
  </div>
)

/* ── Read-only info row (view drawer) ── */
const InfoRow = ({
  icon,
  label,
  value,
  capitalize,
}: {
  icon: React.ReactNode
  label: string
  value?: string | null
  capitalize?: boolean
}) => (
  <div className="flex items-center justify-between px-4 py-3">
    <span className="flex items-center gap-1.5 text-xs text-gray-400">
      {icon}
      {label}
    </span>
    <span className={`text-sm text-gray-700 ${capitalize ? "capitalize" : ""}`}>
      {value || <span className="text-gray-300">—</span>}
    </span>
  </div>
)